import { circle } from '@turf/turf';
import type { GeoJSONSource } from 'mapbox-gl';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { useCdcStore } from '../stores/useCdcStore';

const CDC_RADIUS_SOURCE = 'cdc-nearby-radius-source';
const CDC_RADIUS_FILL_LAYER = 'cdc-nearby-radius-fill';
const CDC_RADIUS_LINE_LAYER = 'cdc-nearby-radius-line';

/**
 * CdcNearbyRadius - Draws a geodesic circle around the selected vessel
 * showing the search radius when the "Nearby" filter is active.
 * Uses Turf.js to generate a proper geodesic circle that accounts
 * for the Earth's spherical shape.
 */
export const CdcNearbyRadius: React.FC = () => {
  const { map, isMapReady } = useMapbox();
  const [layersAdded, setLayersAdded] = useState(false);

  const activeFilters = useCdcStore((state) => state.activeRelatedFilters);
  const selectedVesselCoords = useCdcStore((state) => state.selectedVesselCoords);
  const nearbyRadiusKm = useCdcStore((state) => state.nearbyRadiusKm);

  // Only show circle when nearby filter is active and we have vessel coordinates
  const shouldShowCircle = activeFilters.nearby && selectedVesselCoords !== null;

  // Generate geodesic circle GeoJSON using Turf.js
  const circleGeoJson = useMemo(() => {
    if (!shouldShowCircle || !selectedVesselCoords) {
      return {
        type: 'FeatureCollection' as const,
        features: [],
      };
    }

    // Turf.circle creates a geodesic (great circle) polygon
    // Steps: 64 gives a smooth circle appearance
    const circleFeature = circle([selectedVesselCoords.lon, selectedVesselCoords.lat], nearbyRadiusKm, {
      steps: 64,
      units: 'kilometers',
    });

    return {
      type: 'FeatureCollection' as const,
      features: [circleFeature],
    };
  }, [shouldShowCircle, selectedVesselCoords, nearbyRadiusKm]);

  // Add/update layers
  useEffect(() => {
    if (!isMapReady || !map.current) return;

    const mapInstance = map.current;

    // Add source and layers if not already added
    if (!layersAdded) {
      // Add source
      if (!mapInstance.getSource(CDC_RADIUS_SOURCE)) {
        mapInstance.addSource(CDC_RADIUS_SOURCE, {
          type: 'geojson',
          data: circleGeoJson,
        });
      }

      // Add fill layer (semi-transparent yellow)
      if (!mapInstance.getLayer(CDC_RADIUS_FILL_LAYER)) {
        // Try to add before vessel halo layer if it exists, otherwise just add
        const beforeLayer = mapInstance.getLayer('cdc-vessels-halo') ? 'cdc-vessels-halo' : undefined;
        mapInstance.addLayer(
          {
            id: CDC_RADIUS_FILL_LAYER,
            type: 'fill',
            source: CDC_RADIUS_SOURCE,
            paint: {
              'fill-color': '#FFD700',
              'fill-opacity': 0.1,
            },
          },
          beforeLayer,
        );
      }

      // Add dashed line layer (yellow stroke)
      if (!mapInstance.getLayer(CDC_RADIUS_LINE_LAYER)) {
        const beforeLayer = mapInstance.getLayer('cdc-vessels-halo') ? 'cdc-vessels-halo' : undefined;
        mapInstance.addLayer(
          {
            id: CDC_RADIUS_LINE_LAYER,
            type: 'line',
            source: CDC_RADIUS_SOURCE,
            paint: {
              'line-color': '#FFD700',
              'line-width': 2,
              'line-dasharray': [4, 4],
              'line-opacity': 0.8,
            },
          },
          beforeLayer,
        );
      }

      setLayersAdded(true);
    } else {
      // Update source data
      const source = mapInstance.getSource(CDC_RADIUS_SOURCE) as GeoJSONSource | undefined;
      if (source) {
        source.setData(circleGeoJson);
      }
    }
  }, [isMapReady, map, circleGeoJson, layersAdded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map.current) return;
      const mapInstance = map.current;

      try {
        if (mapInstance.getLayer(CDC_RADIUS_LINE_LAYER)) {
          mapInstance.removeLayer(CDC_RADIUS_LINE_LAYER);
        }
        if (mapInstance.getLayer(CDC_RADIUS_FILL_LAYER)) {
          mapInstance.removeLayer(CDC_RADIUS_FILL_LAYER);
        }
        if (mapInstance.getSource(CDC_RADIUS_SOURCE)) {
          mapInstance.removeSource(CDC_RADIUS_SOURCE);
        }
      } catch {
        // Map may already be destroyed
      }
    };
  }, [map]);

  return null; // This component only manages map side effects
};
